/* eslint-disable no-restricted-syntax */
import React, { createContext, useContext, useState, useRef } from 'react';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { toasterMsg } from '../../../Components/Toaster/Toaster';

export const StateContext = createContext(null);

export default function CareUserInstructionStateProvider({ children }) {
  const { t } = useTranslation('common');
  const [careTabs, setCareTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [imageRowIndex, setImageRowIndex] = useState(null);
  const [ids, setIds] = useState(null);

  const [userState, setUserState] = useState({
    editingRowId: '',
    showImageUpload: false,
    addNewRow: false,
    selectedImageRowIndex: '',
    selectedImageRowColumnId: '',
    rowImage: null,
    showImgDeleteModal: false,
    tabCodes: null,
  });

  const validateFields = (tabId) => {
    let error = true;
    const tabsData = cloneDeep(careTabs);
    for (const tab of tabsData) {
      if (tabId === tab.tabId) {
        const mandatoryColumns = tab?.tabColumns?.filter((column) => {
          return column?.columnDetails?.columnType?.key !== 'Status' && column?.columnDetails?.isRequired;
        });
        if (!tab.tabDetails.find((el) => el.isNew)) {
          error = true;
        } else {
          const requiredColumns = mandatoryColumns?.map((e) => e._id);
          // eslint-disable-next-line no-loop-func
          const hasValue = requiredColumns?.every((element) => {
            return !!tab.tabDetails?.find((e) => e?.columnId === element);
          });
          if (!hasValue) {
            error = false;
          } else {
            // eslint-disable-next-line no-loop-func
            tab?.tabDetails?.forEach((element) => {
              if (requiredColumns?.includes(element?.columnId) && element?.isNew) {
                if (element?.value) {
                  const mandatoryColumnTypes = mandatoryColumns?.find((e) => e._id === element?.columnId)?.columnDetails;
                  if (mandatoryColumnTypes?.columnType?.key === 'Textbox' && element?.value?.length > mandatoryColumnTypes?.characterLimit) {
                    error = false;
                    element.hasError = true;
                  }
                } else {
                  error = false;
                  element.hasError = true;
                }
              }
            });
          }
        }
      }
    }
    setCareTabs(tabsData);
    return error;
  };

  const submitValidate = () => {
    let error = true;
    const tabsData = cloneDeep(careTabs);
    for (const tab of tabsData) {
      const mandatoryColumns = tab?.tabColumns?.filter((column) => {
        return column?.columnDetails?.columnType?.key !== 'Status' && column?.columnDetails?.isRequired;
      });

      const requiredColumns = mandatoryColumns?.map((e) => e._id);
      // eslint-disable-next-line no-loop-func
      const hasValue = requiredColumns?.every((element) => {
        return !!tab.tabDetails?.find((e) => e?.columnId === element);
      });
      if (hasValue) {
        // eslint-disable-next-line no-loop-func
        tab?.tabDetails?.forEach((element) => {
          if (requiredColumns?.includes(element?.columnId)) {
            if (element?.value) {
              const mandatoryColumnTypes = mandatoryColumns?.find((e) => e._id === element?.columnId)?.columnDetails;
              if (mandatoryColumnTypes?.columnType?.key === 'Textbox' && element?.value?.length > mandatoryColumnTypes?.characterLimit) {
                error = false;
                element.hasError = true;
              }
            } else {
              error = false;
              element.hasError = true;
            }
          }
        });
      }
    }
    setCareTabs(tabsData);
    return error;
  };
  const handleImageUpload = (rowIndex, columnId, value, dataRowIndex, tabId) => {
    setUserState((prevState) => ({
      ...prevState,
      showImageUpload: true,
      selectedImageRowIndex: rowIndex,
      selectedImageRowColumnId: columnId,
      rowImage: value,
    }));
    setActiveTab(tabId);
    setImageRowIndex(dataRowIndex);
  };
  const handleImageDelete = (rowIndex, columnId, value, dataRowIndex, tabId) => {
    setUserState((prevState) => ({
      ...prevState,
      showImgDeleteModal: true,
      selectedImageRowIndex: rowIndex,
      selectedImageRowColumnId: columnId,
      rowImage: null,
    }));
    setActiveTab(tabId);
    setImageRowIndex(dataRowIndex);
  };

  const deleteImgConfirmed = () => {
    const copyTabData = cloneDeep(careTabs?.find((it) => it.tabId === activeTab)?.tabDetails);
    copyTabData?.forEach((element) => {
      if (element?.columnId === userState?.selectedImageRowColumnId && imageRowIndex === element?.rowIndex) {
        element.value = { image: '', fullImage: '' };
      }
    });
    const updatedCareLabelList = careTabs.map((item) => {
      if (item.tabId === activeTab) {
        return {
          ...item,
          tabDetails: copyTabData, // Replace with filtered data
        };
      }
      return item;
    });

    setCareTabs(updatedCareLabelList);
    setUserState((prevState) => ({
      ...prevState,
      showImgDeleteModal: false,
      selectedImageRowIndex: '',
      selectedImageRowColumnId: '',
    }));
  };

  const closeImgDeleteModal = () => {
    setUserState((prevState) => ({
      ...prevState,
      showImgDeleteModal: false,
      selectedImageRowIndex: '',
      selectedImageRowColumnId: '',
      rowImage: null,
    }));
  };

  const handleSubmitUpload = async (type, childRef) => {
    if (type !== 'close') {
      const croppedFileUrl = await childRef?.current?.showCroppedImg();
      if (croppedFileUrl) {
        const { imageRes, originalImg } = await croppedFileUrl;
        const originalImgRes = await originalImg;
        const copyTabData = cloneDeep(careTabs?.find((it) => it.tabId === activeTab)?.tabDetails);

        copyTabData?.forEach((element) => {
          if (element?.columnId === userState?.selectedImageRowColumnId && imageRowIndex === element?.rowIndex) {
            element.value = { image: imageRes, fullImage: originalImgRes };
          }
        });
        const updatedCareLabelList = careTabs.map((item) => {
          if (item.tabId === activeTab) {
            return {
              ...item,
              tabDetails: copyTabData, // Replace with filtered data
            };
          }
          return item;
        });

        setCareTabs(updatedCareLabelList);
        setUserState((prevState) => ({
          ...prevState,
          showImageUpload: false,
        }));
      } else {
        toasterMsg(t('SPEC_TEMP_LIBRARY.IMAGE_REQUIRED'), 'danger');
      }
    } else {
      setUserState((prevData) => ({
        ...prevData,
        showImageUpload: false,
        selectedImageRowIndex: '',
        selectedImageRowColumnId: '',
        rowImage: null,
      }));
    }
  };

  const contextValue = {
    careTabs,
    setCareTabs,
    validateFields,
    setActiveTab,
    activeTab,
    imageRowIndex,
    setImageRowIndex,
    useRef,
    userState,
    handleSubmitUpload,
    setUserState,
    handleImageDelete,
    handleImageUpload,
    submitValidate,
    ids,
    setIds,
    deleteImgConfirmed,
    closeImgDeleteModal,
  };

  return (
    <StateContext.Provider
      value={{
        ...contextValue,
      }}
    >
      {children}
    </StateContext.Provider>
  );
}

export function useSuggestionProvider() {
  const { t } = useTranslation('common');
  const context = useContext(StateContext);
  if (!context) {
    throw new Error(t('TAKING_DATES.USE_STATE_MESSAGE'));
  }
  return context;
}
